import { UseInfiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";

import { PaginatedResult } from "@/interfaces/common/PaginatedResult";
import { Product } from "@/interfaces/domain/Product";
import { Promotion } from "@/interfaces/domain/Promotion";
import { getPromotionsRequest, getStorePromotionProductsRequest } from "@/services/api/promotion";
import { logger } from "@/services/logger";
import { formatProductToOrderItem } from "@/utils/helpers";

export const PROMOTIONS_QUERY_KEY = (storeTypeId?: string) => `/promotions/${storeTypeId}`;

export const usePromotions = (
  storeTypeId?: string,
  options?: UseInfiniteQueryOptions<PaginatedResult<Promotion>>
) => {
  const { data, ...rest } = useInfiniteQuery<PaginatedResult<Promotion>>({
    queryKey: [PROMOTIONS_QUERY_KEY(storeTypeId)],
    queryFn: ({ pageParam = 1 }) =>
      getPromotionsRequest({
        page: pageParam,
        storeTypeId,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
    },
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
  return {
    data: data?.pages?.map((page) => page.docs).flat(),
    ...rest,
  };
};

export const usePromotionProducts = ({
  storeId,
  limit,
  options,
}: {
  storeId: string;
  limit: number;
  options?: UseInfiniteQueryOptions<PaginatedResult<Product>>;
}) => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery<
    PaginatedResult<Product>
  >({
    queryKey: [`promotions-products/${storeId}`],
    queryFn: ({ pageParam = 1 }) =>
      getStorePromotionProductsRequest({
        page: pageParam,
        limit,
        storeId,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
    },
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
  return {
    data: data?.pages
      ?.map((page) => page.docs)
      .flat()
      .map(formatProductToOrderItem),
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
};
